import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AppBody } from "../AppBody";
import Breadcrumbs from "../../containers/Breadcrumbs";
import {
  appHeaderTitle,
  removeUrlParameter,
  sortObjValue,
  getCookiebyName,
  sodVariableDefinitionData,
} from "../../common/services/Utilities";
import BackButton from "../../containers/BackButton";
import {
  KeyValue,
  Metadata,
  Query,
  SimpleTableRow,
  TableHeader,
} from "../../interfaces";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl, AppUrl } from "../../config";
import MoreOptionsMenu from "./MoreOptionsMenu";
import Edit from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import LoadingIndicator from "../LoadingIndicator";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import PeerGroupCriteriaTable from "./PeerGroupCriteriaTable";
import ModalDialog from "../ModalDialog";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import { Buffer } from "buffer";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import ResultPagination from "../ResultPagination";
import usePagination from "../../hooks/usePagination";
import { ShareReportModal } from "../ShareReportModal";
import LoadReportModal from "../../containers/LoadReportModal";

const PREFIX = "ManageSavedPeerGroups";

const classes = {
  button: `${PREFIX}-button`,
  divider: `${PREFIX}-divider`,
  deleteDialogContent: `${PREFIX}-deleteDialogContent`,
  deleteDialog: `${PREFIX}-deleteDialog`,
  popperButtons: `${PREFIX}-popperButtons`,
  modifyButton: `${PREFIX}-modifyButton`,
  savedListName: `${PREFIX}-savedListName`,
  footnote: `${PREFIX}-footnote`,
  makeReportButton: `${PREFIX}-makeReportButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.button}`]: {
    alignItems: "center",
    fontSize: "1rem",
    padding: 0,
    fontWeight: 400,
    margin: 0,
    width: 280,
    height: 40,
  },

  [`& .${classes.divider}`]: {
    backgroundColor: "#1a4480",
    marginTop: ".5rem",
  },

  [`& .${classes.deleteDialogContent}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.makeReportButton}`]: {
    width: 240,
    height: 40,
    fontSize: "1rem",
    padding: 0,
    fontWeight: 400,
  },

  [`& .${classes.deleteDialog}`]: {
    "textAlign": "center",
    "& button": {
      height: 40,
      width: 150,
      fontSize: "1rem",
    },
    "& div#delete-dialog-title h2": {
      fontSize: "1.75rem",
    },
  },

  [`& .${classes.popperButtons}`]: {
    "display": "block",
    "marginTop": 0,
    "width": "100%",
    "textAlign": "left",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.modifyButton}`]: {
    "wordBreak": "break-word",
    "padding": ".25rem",
    "margin": ".25rem 0",
    "borderRadius": 5,
    "float": "right",
    "&:hover, &.Mui-focusVisible": {
      borderRadius: 50,
    },
  },

  [`& .${classes.savedListName}`]: {
    "& > span": {
      color: theme.palette.primary.main,
      fontSize: "1rem",
    },
  },

  [`& .${classes.footnote}`]: { marginTop: 16 },
}));

interface Props {
  appState: Query;
  setSavedReportId: (id: string) => void;
  updateTab: (tab: string) => void;
  banner: string;
  updateUi: (name: string, value: any) => void;
}

interface Headers {
  title: string;
  apiField: string;
}

export default function ManageSavedPeerGroups({
  appState,
  setSavedReportId,
  updateTab,
  banner,
  updateUi,
}: Props) {
  const user = getCookiebyName("id_token");
  const headerMetaData: Headers[] = [
    { title: "Line Item Description", apiField: "label" },
    { title: "Variable", apiField: "variable_ytd" },
  ];
  const sodDownloadConfigHeaderMetaData: Headers[] = [
    { title: "Line Item Description", apiField: "label" },
    { title: "Structure Scope", apiField: "structure_scope" },
    { title: "Variable", apiField: "variable_ytd" },
  ];
  const reportHeaders: TableHeader[][] = [
    headerMetaData.map((element) => ({
      title: element.title,
      styles: { textAlign: "left" },
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const sodDownloadConfigHeader: TableHeader[][] = [
    sodDownloadConfigHeaderMetaData.map((element) => ({
      title: element.title,
      styles: { textAlign: "left" },
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const transformDataToTableBodyData = (
    tableData: KeyValue[],
    sod?: boolean,
  ) => {
    return tableData.map((data: KeyValue) => {
      const tableCellData: SimpleTableRow = {};
      (sod ? sodDownloadConfigHeaderMetaData : headerMetaData)
        .map((value: Headers) => value.apiField)
        .forEach((apiField: string) => {
          tableCellData[apiField] = {
            value: data[apiField],
            styles: { verticalAlign: "top", textAlign: "left" },
          };
        });
      return tableCellData;
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [locationState] = React.useState(location);
  const [backButtonState] = React.useState(() => {
    if (location.state?.url?.includes("SOD")) return "SOD";
    if (location.state?.history?.includes("FR")) return "FR";
    return "PGC";
  });
  const [savedGroups, setSavedGroups] = React.useState<KeyValue[]>([]);
  const [fetchGroups, setFetchGroups] = React.useState(true);
  const [savedReports, setSavedReports] = React.useState<KeyValue[]>([]);
  const [fetchReports, setFetchReports] = React.useState(true);
  const [fetchDownloads, setFetchDownloads] = React.useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState({
    open: false,
    value: "",
    endpoint: "",
  });
  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [sodDownloadConfigsLoading, setSodDownloadConfigsLoading] =
    React.useState(false);
  const [displayReportLoading, setDisplayReportLoading] = React.useState(false);
  const [itemId, setItemId] = React.useState<string>("");
  const [tempRenameValue, setTempRenameValue] = React.useState<string>("");
  const [selectedPeerGroups, setSelectedPeerGroups] = React.useState<
    KeyValue[]
  >([]);
  const [savedSODDownloadConfigs, setSavedSODDownloadConfigs] = React.useState<
    KeyValue[]
  >([]);
  const [openGroupModal, setOpenGroupModal] = React.useState(false);
  const [openReportModal, setOpenReportModal] = React.useState(false);
  const [openSODDownloadModal, setOpenSODDownloadModal] = React.useState(false);
  const [currentGroup, setCurrentGroup] = React.useState({
    filters: [{}] as KeyValue[],
    title: "",
    custom: false,
    appState: undefined,
    isMulti: false,
  });
  const [currentReport, setCurrentReport] = React.useState({
    structureData: [] as KeyValue[],
    title: "",
    custom: false,
  });
  const [currentSODDownload, setCurrentSODDownload] = React.useState<KeyValue>(
    {},
  );

  const [initialLoad, setInitialLoad] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState(
    user ? "Custom Financial Reports" : "",
  );

  const [customFRMetaData, setCustomFRMetaData] = React.useState<Metadata>({
    count: 0,
  });
  const [customDownloadMetaData, setCustomDownloadMetaData] =
    React.useState<Metadata>({
      count: 0,
    });
  const [customPGMetaData, setCustomPGMetaData] = React.useState<Metadata>({
    count: 0,
  });

  React.useEffect(() => {
    if (initialLoad && appState.ui.tab) {
      setInitialLoad(false);
      if (appState.ui.tab !== activeTab) {
        setActiveTab(appState.ui.tab);
      }
    }
  }, [activeTab, appState.ui.tab, initialLoad]);
  const currentCount =
    activeTab === "Custom Peer Groups"
      ? customFRMetaData.count
      : activeTab === "Custom Financial Reports"
        ? customFRMetaData.count
        : customDownloadMetaData.count;
  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25, currentCount, activeTab);

  function openGroupTable(index: number) {
    setOpenGroupModal(true);
    setCurrentGroup({
      filters: selectedPeerGroups[index].filter_criteria,
      title: selectedPeerGroups[index].name,
      custom: selectedPeerGroups[index].is_custom,
      isMulti: selectedPeerGroups[index].isMultiCert,
      appState: selectedPeerGroups[index].saved_state
        ? JSON.parse(
            Buffer.from(
              selectedPeerGroups[index].saved_state,
              "base64",
            ).toString("utf-8"),
          )
        : undefined,
    });
  }
  function openReportTable(index: number) {
    setOpenReportModal(true);
    setCurrentReport({
      structureData: transformDataToTableBodyData(
        savedReports[index].structure[0].fields,
      ),
      title: savedReports[index].title,
      custom: savedReports[index].is_custom,
    });
  }
  function openSODDownloadConfigTable(index: number) {
    setOpenSODDownloadModal(true);

    const variables = savedSODDownloadConfigs[index].saved_state.variable[0];
    const location = savedSODDownloadConfigs[index].saved_state.location;
    let result: KeyValue[] = [];

    sodVariableDefinitionData.forEach((data: KeyValue) => {
      data.children.forEach((child: KeyValue) => {
        if (variables.includes(child.variable)) {
          result.push({
            variable_ytd: child.variable,
            label: child.title,
            structure_scope: child.hasOwnProperty("SODType")
              ? child.SODType
              : "",
          });
        }
      });
    });
    setCurrentSODDownload({
      data: transformDataToTableBodyData(result, true),
      title: savedSODDownloadConfigs[index].name,
      locations: location,
    });
  }
  function editGroupsButton(value: KeyValue) {
    if (value.isMultiCert) {
      navigate({
        pathname: `${AppUrl("PeerGroup")}/definegroup`,

        search: `?createGroupType=cert&savedGroupId=${value.ID}`,
      });
    } else {
      navigate({
        pathname: `${AppUrl("PeerGroup")}/definegroup`,

        search: `?createGroupType=search&savedGroupId=${value.ID}`,
      });
    }
  }
  function editReportButton(value: KeyValue) {
    setSavedReportId(value.ID);
    navigate(
      {
        pathname: `${AppUrl("PeerGroup")}/definereport`,
      },
      {},
    );
  }
  function editDownloadConfigs(value: KeyValue) {
    navigate(
      {
        pathname: `${AppUrl("SOD")}/customDownload`,
      },
      {
        name: `${value.name}`,
        state: {
          url: AppUrl("SOD") + window.location.search,
          history: "peergroup",
          name: `${value.name}`,
        },
      },
    );
  }
  const reportTableBody = (
    <SimpleTableBody tableData={currentReport.structureData} />
  );

  const sodDownloadTableBody = (
    <SimpleTableBody tableData={currentSODDownload.data} />
  );

  const [closeDialog, setCloseDialog] = React.useState(false);
  const contentType =
    activeTab === "Custom SOD Downloads"
      ? "SOD Download Config"
      : activeTab === "Custom Financial Reports"
        ? "Custom Report"
        : activeTab.slice(0, -1);
  const handleDeleteClose = () =>
    setOpenDeleteDialog({ open: false, value: "", endpoint: "" });
  const deleteDialog = (
    <Dialog
      id="delete-dialog"
      onClose={handleDeleteClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      open={openDeleteDialog.open}
      className={classes.deleteDialog}
    >
      <DialogContent className={classes.deleteDialogContent}>
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            color: "#1a4480",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          Delete Saved {contentType}?
        </DialogTitle>
        <DialogContentText
          variant="body2"
          color="primary.main"
          id="delete-dialog-description"
          sx={{ textAlign: "center" }}
        >
          You are about to delete this Saved {contentType}. This cannot be
          undone. Once deleted you will have to recreate this {contentType} from
          scratch.
        </DialogContentText>
        <DialogTitle
          id="delete-dialog-subtitle"
          sx={{ color: "#1a4480", textAlign: "center", fontWeight: 700 }}
        >
          Do you wish to continue?
        </DialogTitle>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Button
              onClick={handleDeleteClose}
              color="secondary"
              variant="contained"
              id="cancel-delete"
              sx={{ minWidth: 150, minHeight: 40 }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid
            size={{
              sm: 6,
              xs: 12,
            }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Button
              variant="contained"
              onClick={() => {
                deleteReportOrGroup(
                  openDeleteDialog.value,
                  openDeleteDialog.endpoint,
                );
                handleDeleteClose();
              }}
              color="primary"
              id="continue-delete"
              sx={{ minWidth: 150, minHeight: 40 }}
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );

  const [getSharedReport, setGetSharedReport] = React.useState<any>(false);
  const [sharedKey, setSharedKey] = React.useState<string>("");
  const [updateReportIndex, setUpdateReportIndex] =
    React.useState<boolean>(false);
  const [showSharedKeyModal, setShowSharedKeyModal] =
    React.useState<boolean>(false);
  const createSharedKey = (): string => {
    const timePart = Date.now().toString(36);
    const randomPart = Math.random().toString(36).slice(2, 8);
    return `${timePart}-${randomPart}`;
  };

  React.useEffect(() => {
    if (getSharedReport?.structure?.length) {
      if (getSharedReport?.sharedKey) {
        setSharedKey(getSharedReport?.sharedKey);
        setShowSharedKeyModal(true);
      } else {
        const key = createSharedKey();
        (async () => {
          await fetchData(
            `${ApiBaseUrl()}/shared_reports`,
            apiPost(
              {
                sharedKey: key,
                structure: getSharedReport.structure,
              },
              { Authorization: `Bearer ${user}` },
            ),
          )
            .then((response) => {
              // next we want to use the shared key to update the custom report index
              setSharedKey(key);
              setUpdateReportIndex(true);
            })
            .catch((error) => {
              console.error(`${error.name} : ${error.message}`);
            });
        })();
      }
    }
  }, [getSharedReport, user]);

  React.useEffect(() => {
    //this updates the existing report with the new shared key
    if (
      updateReportIndex &&
      sharedKey &&
      getSharedReport?.structure?.length &&
      getSharedReport?.ID
    ) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/report_types/update-by-shared-key`,
          apiPost(
            {
              sharedKey: sharedKey,
              ID: getSharedReport.ID,
            },
            { Authorization: `Bearer ${user}` },
          ),
        )
          .then((response) => {
            setGetSharedReport(false);
            setShowSharedKeyModal(true);
            setUpdateReportIndex(false);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [
    getSharedReport.ID,
    getSharedReport.structure,
    sharedKey,
    updateReportIndex,
    user,
  ]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reloadReports = urlParams.get("reloadReports") ?? "";
    const reloadGroups = urlParams.get("reloadGroups") ?? "";
    const reloadConfigs =
      (locationState?.reload || location?.state?.reload === "reloadConfigs") ??
      "";
    if (reloadReports) {
      navigate(removeUrlParameter(window.location.href, ["reloadReports"]));
      setDisplayReportLoading(true);
      const timer = setTimeout(() => {
        setFetchReports(true);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (reloadGroups) {
      navigate(removeUrlParameter(window.location.href, ["reloadGroups"]));
      setGroupsLoading(true);
      setActiveTab("Custom Peer Groups");
      const timer = setTimeout(() => {
        setFetchGroups(true);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (reloadConfigs) {
      navigate(removeUrlParameter(window.location.href, ["reloadConfigs"]));
      setSodDownloadConfigsLoading(true);
      setActiveTab("Custom SOD Downloads");
      const timer = setTimeout(() => {
        setFetchDownloads(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [navigate, locationState, location?.state?.reload]);

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && user && activeTab === "Custom Peer Groups") {
      setFetchGroups(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups/my_groups`,
          apiPost(
            {
              filters: "is_custom:true",
              limit: rowsPerPage,
              offset: (page - 1) * rowsPerPage,
            },
            { Authorization: `Bearer ${user}` },
          ),
        )
          .then((response) => {
            setCustomPGMetaData({ count: response.totals.count });
            setCloseDialog(false);
            setSavedGroups(sortObjValue(response.data, "name"));
            setSelectedPeerGroups(response.data);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            const timer = setTimeout(() => {
              setGroupsLoading(false);
            }, 200);
            return () => {
              clearTimeout(timer);
            };
          });
      })();
    }
  }, [fetchGroups, page, rowsPerPage, groupsLoading, user, activeTab]);

  React.useEffect(() => {
    let isMounted = true;

    if (isMounted && user && activeTab === "Custom SOD Downloads") {
      setFetchDownloads(false);
      setSodDownloadConfigsLoading(true);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/custom_sod/my_custom_sod?limit=${rowsPerPage}&offset=${(page - 1) * rowsPerPage}`,
          {},
          {
            Authorization: `Bearer ${getCookiebyName("id_token")}`,
          },
        )
          .then((response) => {
            setCustomDownloadMetaData({ count: response.totals.count });
            setCloseDialog(false);
            setSavedSODDownloadConfigs(sortObjValue(response.data, "name"));
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            const timer = setTimeout(() => {
              setSodDownloadConfigsLoading(false);
            }, 200);
            return () => {
              clearTimeout(timer);
            };
          });
      })();
    }
  }, [user, page, rowsPerPage, fetchDownloads, activeTab]);

  React.useEffect(() => {
    let isMounted = true;
    setFetchReports(false);
    if (isMounted && user && activeTab === "Custom Financial Reports") {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/report_types/my_reports`,
          apiPost(
            {
              filters: "is_custom:true",
              limit: rowsPerPage,
              offset: (page - 1) * rowsPerPage,
            },
            { Authorization: `Bearer ${user}` },
          ),
        )
          .then((response) => {
            setCustomFRMetaData({ count: response.totals.count });
            setSavedReports(sortObjValue(response.data, "title"));
            updateUi(
              "savedReportNames",
              response.data.map((report: any) => report.title),
            );
            setCloseDialog(false);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            const timer = setTimeout(() => {
              setDisplayReportLoading(false);
            }, 200);
            return () => {
              isMounted = false;
              clearTimeout(timer);
            };
          });
      })();
    }
  }, [
    displayReportLoading,
    fetchReports,
    groupsLoading,
    updateUi,
    user,
    page,
    rowsPerPage,
    activeTab,
  ]);
  const deleteReportOrGroup = (id: string, endpoint: string) => {
    const deleteID = {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${user}`,
        "Content-Type": "application/json",
        "x-auth": "bfs",
        "x-rate-limit": "off",
      },
    };
    fetch(`${ApiBaseUrl()}/${endpoint}/${id}`, deleteID).then(() => {
      endpoint === "peer_groups"
        ? setFetchGroups(true)
        : endpoint === "report_types"
          ? setFetchReports(true)
          : setFetchDownloads(true);
    });
  };

  const postFetch = (endpoint: string, post: KeyValue) => {
    fetch(`${ApiBaseUrl()}/${endpoint}`, post).then(() => {
      endpoint === "peer_groups"
        ? setFetchGroups(true)
        : endpoint === "report_types"
          ? setFetchReports(true)
          : setFetchDownloads(true);
    });
  };
  const renamePost = (value: KeyValue, endpoint: string, newName: string) => {
    const name = {
      [endpoint === "peer_groups" || endpoint === "custom_sod"
        ? "name"
        : "title"]: newName,
    };
    const renamePostReq = apiPost(
      { ...value, ...name, ID: value.ID },
      { "x-auth": "bfs", "Authorization": `Bearer ${user}` },
    );
    postFetch(endpoint, renamePostReq);
  };
  const createPost = (value: KeyValue, endpoint: string) => {
    setCloseDialog(true);
    const groupLookup: KeyValue = {
      report_types: savedReports.map((report) => report.title),
      peer_groups: savedGroups.map((group) => group.name),
      custom_sod: savedSODDownloadConfigs.map((config) => config.name),
    };
    //this removes sharedKey and updatedAt when duplicating, as we don't want to carry those to duplicates
    const { sharedKey, updatedAt, ...cleanedValue } = value;
    const nameKey =
      endpoint === "peer_groups" || endpoint === "custom_sod"
        ? "name"
        : "title";
    let newName = "";
    if (cleanedValue.is_shared && appState.ui?.savedReportNames?.length) {
      const names = appState.ui.savedReportNames;
      let counter = 1;
      newName = `sharedReport_${counter}`;
      while (names.includes(newName)) {
        counter++;
        newName = `sharedReport_${counter}`;
      }
    } else {
      newName = `${cleanedValue[nameKey]}_copy`;
      while (groupLookup[endpoint].includes(newName)) {
        newName = `${newName}_copy`;
      }
    }

    const createPostReq = apiPost(
      {
        ...cleanedValue,
        [nameKey]: newName,
        ID: `${cleanedValue.created_by}_${Date.now()}`,
      },
      { "x-auth": "bfs", "Authorization": `Bearer ${user}` },
    );
    postFetch(endpoint, createPostReq);
  };
  const editSubmit = (
    newName: string,
    value: KeyValue,
    group?: boolean,
    type?: string,
  ) => {
    const editName = group || type ? value.name : value.title;
    if (newName === editName || !newName) {
      setItemId("");
      setTempRenameValue("");
    } else {
      renamePost(
        value,
        group ? "peer_groups" : type ? "custom_sod" : "report_types",
        newName,
      );
      setItemId("");
      setTempRenameValue("");
    }
  };
  const cancelEdit = () => {
    setItemId("");
    setTempRenameValue("");
  };

  const regexTest = (e: React.ChangeEvent<HTMLInputElement>) =>
    !/[&+#,]/g.test(e.target.value);
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (regexTest(event)) {
      setTempRenameValue(event.target.value);
    }
  };

  const itemListButtons = (
    value: KeyValue,
    endpoint: string,
    index: number,
  ) => {
    const itemName =
      endpoint === "peer_groups" || endpoint === "custom_sod"
        ? value.name
        : value.title;
    const type =
      endpoint === "peer_groups"
        ? "group"
        : endpoint === "report_types"
          ? "report"
          : "custom_sod";
    const openModal = () => {
      return endpoint === "peer_groups"
        ? openGroupTable(index)
        : endpoint === "report_types"
          ? openReportTable(index)
          : openSODDownloadConfigTable(index);
    };
    const editButtonOnClick = () => {
      return endpoint === "peer_groups"
        ? editGroupsButton(value)
        : endpoint === "report_types"
          ? editReportButton(value)
          : editDownloadConfigs(value);
    };

    return (
      <ListItem
        sx={(theme) => ({
          "&:hover, &.Mui-focusVisible": {
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 1,
            color: theme.palette.primary.main,
          },
        })}
      >
        <Grid
          size={{
            md: 10,
          }}
        >
          <ListItemText primary={itemName} className={classes.savedListName} />
        </Grid>
        <Grid
          size={{
            md: 2,
          }}
        >
          <MoreOptionsMenu
            id={`${type}-option-${index}`}
            popperPosition="left-start"
            toolTipText={`More Options ${
              value.title ? value.title : value.name
            }`}
            icon={<MoreHorizIcon />}
            close={closeDialog}
          >
            <Button
              id="view-item"
              className={classes.popperButtons}
              onClick={openModal}
            >
              {endpoint === "peer_groups"
                ? "View Peers & Criteria"
                : endpoint === "report_types"
                  ? "View Selected Variables"
                  : "Preview SOD Download Config"}
            </Button>
            <Button
              id="rename-item"
              className={classes.popperButtons}
              onClick={() => {
                setItemId(value.ID);
                setTempRenameValue(itemName);
              }}
            >
              {endpoint === "peer_groups"
                ? "Rename Peer Group"
                : endpoint === "report_types"
                  ? "Rename Custom Report"
                  : "Rename SOD Download Config"}
            </Button>
            <Button
              id="duplicate-item"
              className={classes.popperButtons}
              onClick={() => createPost(value, endpoint)}
            >
              {endpoint === "peer_groups"
                ? "Duplicate Peer Group"
                : endpoint === "report_types"
                  ? "Duplicate Custom Report"
                  : "Duplicate SOD Download Config"}
            </Button>
            <Button
              id="delete-item"
              className={classes.popperButtons}
              onClick={() => {
                setOpenDeleteDialog({
                  open: true,
                  value: value.ID,
                  endpoint,
                });
              }}
            >
              Delete Item From List
            </Button>
            {endpoint === "report_types" && (
              <Button
                id="share-item"
                className={classes.popperButtons}
                onClick={() => {
                  setGetSharedReport(value);
                }}
              >
                Share Custom Report
              </Button>
            )}
          </MoreOptionsMenu>
        </Grid>
        <Grid
          size={{
            md: 2,
          }}
        >
          <IconButton
            className={classes.modifyButton}
            title={`Edit ${
              endpoint === "peer_groups"
                ? "Group"
                : endpoint === "report_types"
                  ? "Report"
                  : "SOD Download Config"
            }`}
            id={
              endpoint === "peer_groups"
                ? `edit-group-${index}`
                : endpoint === "report_types"
                  ? `edit-report-${index}`
                  : `edit-sod-download-config-${index}`
            }
            onClick={editButtonOnClick}
            size="large"
          >
            <Edit color="primary" />
          </IconButton>
        </Grid>
      </ListItem>
    );
  };

  const duplicateNameCheck = (value: string) => {
    const groupLookup: KeyValue = {
      "Custom Financial Reports": savedReports.map((report) => report.title),
      "Custom Peer Groups": savedGroups.map((group) => group.name),
      "Custom SOD Downloads": savedSODDownloadConfigs.map(
        (config) => config.name,
      ),
    };
    return groupLookup[activeTab]
      .reduce(
        (acc: string[], name: string) =>
          name !== value ? [...acc, name] : acc,
        [],
      )
      .includes(tempRenameValue);
  };

  const editForm = (
    value: KeyValue,
    index: number,
    group?: boolean,
    type?: string,
  ) => {
    const nameIsDuplicate = duplicateNameCheck(
      activeTab === "Custom Financial Reports" ? value.title : value.name,
    );
    return (
      <form
        key={index}
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          editSubmit(tempRenameValue, value, group, type);
        }}
      >
        <FormControl fullWidth>
          <TextField
            fullWidth
            id="modifyTextField"
            label={`Rename ${group ? "Peer Group" : contentType}`}
            variant="outlined"
            value={tempRenameValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNameChange(e)
            }
            error={nameIsDuplicate}
            aria-errormessage="wow"
            slotProps={{
              input: {
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        id="cancelEdit"
                        title="Cancel Edit"
                        onClick={cancelEdit}
                        size="large"
                      >
                        <ClearIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                    <InputAdornment position="end">
                      <IconButton
                        id="saveEdit"
                        type="submit"
                        title="Save Edit"
                        size="large"
                        disabled={nameIsDuplicate}
                        color="primary"
                      >
                        <CheckIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              },
            }}
          />
          {nameIsDuplicate && (
            <Alert
              id="certs-required"
              severity="error"
              style={{ padding: "0 .5rem" }}
            >
              Name already in use.
            </Alert>
          )}
        </FormControl>
      </form>
    );
  };
  const LoadingSpinner = () => (
    <LoadingIndicator
      spinnerSx={{ textAlign: "center", marginTop: "2rem" }}
      textSx={{ textAlign: "center", marginTop: "2rem" }}
      spinnerSize={50}
      text="Loading"
    />
  );
  const SavedPeerGroupsList = () => {
    return (
      <Grid container sx={{ display: "block", width: "100%" }}>
        {groupsLoading ? (
          <LoadingSpinner />
        ) : (
          <ResultPagination
            count={customPGMetaData.count}
            handleChangePage={handleChangePage as any}
            handlePickPage={handlePageJump}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            limit={rowsPerPage}
            pageNumber={page}
          >
            <List>
              {savedGroups.map((value: KeyValue, index) => {
                const labelId = `group-${index}`;

                return (
                  <div key={labelId} id={labelId}>
                    {itemId === value.ID
                      ? editForm(value, index, true)
                      : itemListButtons(value, "peer_groups", index)}
                  </div>
                );
              })}
            </List>
          </ResultPagination>
        )}
      </Grid>
    );
  };

  const SavedSODDownloadConfigs = () => {
    return (
      <Grid container style={{ display: "block", width: "100%" }}>
        {sodDownloadConfigsLoading ? (
          <LoadingSpinner />
        ) : (
          <ResultPagination
            count={customDownloadMetaData.count}
            handleChangePage={handleChangePage as any}
            handlePickPage={handlePageJump}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            limit={rowsPerPage}
            pageNumber={page}
          >
            <List>
              {savedSODDownloadConfigs.map((value: KeyValue, index) => {
                const labelId = `config-${index}`;
                return (
                  <div key={labelId} id={labelId}>
                    {itemId === value.ID
                      ? editForm(value, index, false, "custom_sod")
                      : itemListButtons(value, "custom_sod", index)}
                  </div>
                );
              })}
            </List>
          </ResultPagination>
        )}
      </Grid>
    );
  };

  const SavedReportsList = () => {
    return (
      <Grid container style={{ display: "block", width: "100%" }}>
        {displayReportLoading ? (
          <LoadingSpinner />
        ) : (
          <ResultPagination
            count={customFRMetaData.count}
            handleChangePage={handleChangePage as any}
            handlePickPage={handlePageJump}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            limit={rowsPerPage}
            pageNumber={page}
          >
            <List>
              {savedReports.map((value: KeyValue, index) => {
                const labelId = `report-${index}`;

                return (
                  <div key={labelId} id={labelId}>
                    {itemId === value.ID
                      ? editForm(value, index, false)
                      : itemListButtons(value, "report_types", index)}
                  </div>
                );
              })}
            </List>
          </ResultPagination>
        )}
      </Grid>
    );
  };
  useInitialPageLoad("manageSavedPeerGroups");
  const button = (label: string, className: string, onClick: () => void) => {
    return (
      <Grid
        container
        sx={{
          justifyContent: "center",
          alignItems: "center",
          marginTop: "1rem",
          marginBottom: "1rem",
        }}
      >
        <Button
          className={className}
          id={label.toLowerCase().replace(/ /g, "-")}
          variant="contained"
          color={"primary"}
          disableElevation
          onClick={onClick}
          disabled={!getCookiebyName("id_token") || banner === "true"}
        >
          {label}
        </Button>
      </Grid>
    );
  };

  const saveReport = (data: KeyValue[] | null) => {
    if (data?.length && data[0]?.fields.length) {
      const value: KeyValue = {
        is_shared: true,
        title: "shared_report",
        created_by: getCookiebyName("uuid"),
        is_custom: true,
        containsDemographics: data[0].fields.some(
          (val: KeyValue) => val.demographicOnly === "TRUE",
        ),
        structure: data,
        ID: `${getCookiebyName("uuid")}_${Date.now()}`,
      };
      createPost(value, "report_types");
      updateUi("loadModal", false);
    }
  };
  const loadReportModal = (
    <div>
      <LoadReportModal
        showLoadReportModal={appState?.ui?.loadModal}
        currentApp={"savedCustomContent"}
        onClose={() => updateUi("loadModal", false)}
        onSubmit={saveReport}
      />
    </div>
  );

  const manageSaved = (title: string, isNotAuthenticated: boolean) => {
    return (
      <Typography
        variant="h2"
        color="primary.main"
        style={{
          opacity: isNotAuthenticated ? 0.6 : 1,
        }}
      >
        {title}
        <Divider
          className={classes.divider}
          style={{ opacity: isNotAuthenticated ? 0.6 : 1 }}
        />
      </Typography>
    );
  };

  const renderContent = () => {
    if (!getCookiebyName("id_token")) {
      return null;
    }

    return (
      <Grid container spacing={4}>
        {activeTab === "Custom Financial Reports" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Create New Custom Financial Reports",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              sx={{
                justifyContent: "center",
                alignItems: "center",
                marginTop: "1rem",
                marginBottom: "1rem",
                gap: 3,
              }}
            >
              <Button
                className={classes.makeReportButton}
                id={"load-code"}
                variant="contained"
                color={"primary"}
                disableElevation
                onClick={() => updateUi("loadModal", true)}
                disabled={!getCookiebyName("id_token")}
              >
                {"Load a Shared Report Code"}
              </Button>
              <Button
                className={classes.makeReportButton}
                id={"define-a-customized-report"}
                variant="contained"
                color={"primary"}
                disableElevation
                onClick={() =>
                  navigate(
                    {
                      pathname: `${AppUrl("PeerGroup")}/definereport`,
                    },
                    {
                      state: {
                        url: `${AppUrl("FinancialReporting")}${window.location.search}`,
                      },
                    },
                  )
                }
                disabled={!getCookiebyName("id_token")}
              >
                {"Define a Customized Report"}
              </Button>
            </Grid>
          </Grid>
        )}
        {activeTab === "Custom Financial Reports" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Manage Your Saved Customized Reports",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              sx={{
                justifyContent: "center",
                alignItems: "center",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              {savedReports.length === 0 && !displayReportLoading ? (
                <Typography variant="body2" color="primary.main">
                  {!getCookiebyName("id_token")
                    ? "You must be signed in to create or see saved custom content."
                    : "You currently have no saved Custom Reports. Build one today!"}
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary.main">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedReportsList()
              )}
            </Grid>
          </Grid>
        )}
        {activeTab === "Custom Peer Groups" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Create New Custom Peer Groups",
              !getCookiebyName("id_token"),
            )}
            {button("Build a Custom Peer Group", classes.button, () =>
              navigate({
                pathname: `${AppUrl("PeerGroup")}/definegroup`,
                state: {
                  url: AppUrl("PeerGroup") + window.location.search,
                },
              }),
            )}
          </Grid>
        )}
        {activeTab === "Custom Peer Groups" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Manage Your Saved Custom Peer Groups",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              sx={{
                justifyContent: "center",
                alignItems: "center",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              {savedGroups.length === 0 ? (
                <Typography variant="body2" color="primary.main">
                  {"You currently have no saved Peer Groups. Build one today!"}
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary.main">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedPeerGroupsList()
              )}
              {savedGroups.length !== 0 && (
                <Typography
                  variant="body2"
                  color="primary.main"
                  className={classes.footnote}
                >
                  <span style={{ fontWeight: "bold" }}>Note:</span> Custom Peer
                  groups do not include inactive institutions or institutions
                  that have dropped insurance for any given reporting period. As
                  a result, they can change over time, as institutions come and
                  go.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
        {activeTab === "Custom SOD Downloads" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Create New Custom SOD Download",
              !getCookiebyName("id_token"),
            )}
            {button("Build a Custom SOD Download", classes.button, () =>
              navigate({
                pathname: `${AppUrl("SOD")}/customDownload`,
                state: {
                  url: AppUrl("SOD") + window.location.search,
                  history: "peergroup",
                },
              }),
            )}
          </Grid>
        )}
        {activeTab === "Custom SOD Downloads" && (
          <Grid
            size={{
              md: 6,
            }}
          >
            {manageSaved(
              "Manage Your Saved SOD Downloads",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              sx={{
                justifyContent: "center",
                alignItems: "center",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              {savedSODDownloadConfigs.length === 0 ? (
                <Typography variant="body2" color="primary.main">
                  {
                    "You currently have no saved SOD Download configurations. Build one today!"
                  }
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary.main">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedSODDownloadConfigs()
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const introParagraph = (
    <>
      {appHeaderTitle("manageSavedPeerGroups")}
      <Typography variant="body2" color="primary.main" style={{ width: 926 }}>
        Maximize your analysis time by creating custom configurations of the
        reports you run most often. You can customize and manage financial
        reports, peer groups, and SOD download sets to focus on the data that is
        most important to you. Customize a wide array of criteria including:
        specific data sets, peer groups (either specific institutions sets or
        those defined by financial parameters, regional locations, business
        specialties), and more.
      </Typography>
      <Typography variant="body2" color="primary.main" style={{ width: 926 }}>
        Once saved, you can access and reuse your custom content again and
        again, saving you the time of not having to redefine them. If you need
        assistance, please see the{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>
          Help section for tutorials
        </RouterLink>{" "}
        to get you started.
      </Typography>
      <Typography
        variant="body2"
        color="primary.main"
        id="peer-group-feedback-link"
      >
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupManage"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
      {!getCookiebyName("id_token") && (
        <>
          <Typography variant="h3" style={{ paddingBottom: 0 }}>
            You must use LogIn.gov to create a personal account in order to
            access the functionality of this page.
          </Typography>
          <Typography
            variant="body2"
            color="primary.main"
            style={{ width: 926 }}
          >
            We use LogIn.gov as a safe and respected method of confirming user
            identity. It is a service of the GSA and is FREE to use. It offers
            single sign-on access to this and a host of other government
            websites. You can use your existing LogIn.gov account to sign-in, or
            you can quickly create one today.
          </Typography>
        </>
      )}
    </>
  );

  const handleClose = () => {
    setShowSharedKeyModal(false);
    setSharedKey("");
    setFetchReports(true);
  };
  const sharedKeyModal = (
    <>
      <ShareReportModal
        sharedKey={sharedKey}
        showShareReportModal={showSharedKeyModal}
        onClose={handleClose}
      />
    </>
  );
  const breadCrumbTitle = "Manage Custom Content";
  return (
    <Root>
      {loadReportModal}
      {sharedKeyModal}
      {deleteDialog}
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle={breadCrumbTitle} />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        introText={introParagraph}
        backButton={
          <BackButton
            app={
              backButtonState === "SOD"
                ? "SOD"
                : backButtonState === "FR"
                  ? "FinancialReporting"
                  : "PeerGroup"
            }
            buttonLabel={
              backButtonState === "SOD"
                ? "Back to SOD"
                : "Back to Financial Reporting"
            }
            variant="dark"
          />
        }
      >
        <br />
        {getCookiebyName("id_token") && (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={false}
                sx={{ borderBottom: 1, borderColor: "divider" }}
                aria-label="tabs"
              >
                {[
                  "Custom Financial Reports",
                  "Custom Peer Groups",
                  "Custom SOD Downloads",
                  // "Custom Charting",
                ].map((tab, index) => (
                  <Tab
                    label={tab}
                    id={tab}
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      updateTab(tab);
                    }}
                    sx={{
                      "marginRight": "15px",
                      "backgroundColor":
                        activeTab === tab ? "#1A4480" : "#f0f0f0",
                      "color": activeTab === tab ? "#ffffff" : "#1A4480",
                      "&:hover": {
                        backgroundColor: activeTab === tab ? "" : "#D9E8F6",
                        color: activeTab === tab ? "#ffffff" : "#1A4480",
                      },
                      "textTransform": "none",
                      "fontFamily": "Source Sans Pro",
                      "fontSize": "16px",
                      "fontWeight": "bold",
                      "width": "333px",
                      "height": "35px",
                    }}
                  />
                ))}
              </Tabs>
              <Box
                sx={{
                  paddingTop: 5,
                }}
              >
                {renderContent()}
              </Box>
            </Box>
          </Box>
        )}
        <ModalDialog
          title={currentGroup.title}
          display={openGroupModal}
          setDisplay={setOpenGroupModal}
          maxWidth="lg"
        >
          <PeerGroupCriteriaTable
            includeReportPeriod
            currentGroup={currentGroup}
            appState={appState}
          />
        </ModalDialog>
        <ModalDialog
          title={currentReport.title}
          display={openReportModal}
          setDisplay={setOpenReportModal}
          maxWidth="lg"
        >
          <MultiHeaderTable
            tableBody={reportTableBody}
            tableHeaders={reportHeaders}
          />
        </ModalDialog>
        <ModalDialog
          title={currentSODDownload.title}
          display={openSODDownloadModal}
          setDisplay={setOpenSODDownloadModal}
          selectedLocations={currentSODDownload.locations}
          maxWidth="lg"
        >
          <MultiHeaderTable
            tableBody={sodDownloadTableBody}
            tableHeaders={sodDownloadConfigHeader}
          />
        </ModalDialog>
      </AppBody>
    </Root>
  );
}
